/**
 * TemplateVoice — the rich grammar + hand-authored fallback FLOOR. Guarantees the
 * town stays populated + pleasant + warm + genuinely conversational with the LLM
 * off, offline, or over-budget (CLAUDE.md; decision D14). Deterministic: seeded via
 * core/rng so the same request always reads the same way, yet varied across many
 * beats. Never mutates canon; never throws.
 *
 * Conversational texture (D14): beyond rendering the beat itself, exchanges weave in
 * peacocking/show-off, mutual flattery, fond bragging, TWO-WAY questions aimed at the
 * player, and lore/know-how tidbits — so the floor reads as flowing talk, not a wall
 * of greetings. Every rendered line is de-duplicated: no exact-duplicate text ever
 * appears twice within one VoiceResponse (the repetition bug that made two NPCs emit
 * the identical "Same here, …" line is structurally impossible now).
 */
import type {
  Voice,
  VoiceRequest,
  VoiceResponse,
  VoiceLine,
  SpeakerContext,
  Beat,
  Emotion,
} from '@/shared';
import type { Rng } from '@/core';
import { makeRng, hashString } from '@/core';
import {
  BEAT_TEMPLATES,
  MEMORY_ASIDES,
  PEACOCK_ASIDES,
  FLATTERY_ASIDES,
  TWO_WAY_PLAYER_QUESTIONS,
  KNOWHOW_ASIDES,
  FOND_BRAG_ASIDES,
  FLOURISHES,
  PLAYER_GREETING_REPLIES,
  PLAYER_QUESTION_REPLIES,
  PLAYER_GENERAL_REPLIES,
  PLAYER_REPLY_FOLLOWUPS,
  CAST_REACTIONS,
  type TemplateEntry,
} from './templates.js';
import { scrubLine, softenEmotion } from './safety.js';

/** Soft fallback species the know-how/lore banks reference when a beat carries none. */
const RARE_FLAVOR: readonly string[] = [
  'Mew',
  'Articuno',
  'Zapdos',
  'Moltres',
  'Lapras',
  'Dragonite',
  'Chansey',
];
/** Soft biome flavor for "where it lives" know-how when a beat carries none. */
const BIOME_FLAVOR: readonly string[] = [
  'deep forest',
  'lakeshore',
  'mountain slope',
  'old ruins',
  'misty meadow',
  'seaside caves',
];
/** Warm nickname stand-ins when a mon has no explicit nickname. */
const NICK_FLAVOR: readonly string[] = [
  'my little buddy',
  'my partner',
  'the team sweetheart',
  'my oldest friend',
];
/** Soft move stand-ins when a cast beat (somehow) carries no move name/id. */
const MOVE_FLAVOR: readonly string[] = [
  'flashy move',
  'dazzling flourish',
  'show-stopper',
  'lovely technique',
];
/** Soft element stand-ins when a cast beat carries no move type. */
const MOVE_TYPE_FLAVOR: readonly string[] = ['Normal', 'Fire', 'Water', 'Grass', 'Electric'];

export class TemplateVoice implements Voice {
  readonly kind = 'template' as const;
  private readonly seed: number;

  constructor(seed = 0) {
    this.seed = seed >>> 0;
  }

  generate(request: VoiceRequest): Promise<VoiceResponse> {
    const lines = this.buildLines(request);
    return Promise.resolve({ lines, source: 'template', tokensUsed: 0 });
  }

  // ── internals ──────────────────────────────────────────────────────────────

  private buildLines(request: VoiceRequest): readonly VoiceLine[] {
    const { speakers, beats, desiredLineCount } = request;
    if (speakers.length === 0) return [];

    // Seed off the request so identical requests reproduce; mixing the beat ids +
    // speakers keeps different requests diverging without any global state.
    const rng = makeRng(this.requestSeed(request));
    const target = Math.max(1, desiredLineCount);

    // Build an interleaved subject/reaction script grounded in the beats, woven with
    // conversational texture (peacock / flattery / two-way / know-how), then top up
    // with ambient chitchat so the chat never feels thin.
    const lines: VoiceLine[] = [];
    const used = new Set<string>();
    const speakerById = new Map(speakers.map((s) => [s.trainerId, s]));

    const orderedBeats = beats.length > 0 ? beats : [this.ambientBeat()];

    let beatIdx = 0;
    let stagnation = 0;
    while (lines.length < target) {
      const before = lines.length;
      const beat = orderedBeats[beatIdx % orderedBeats.length]!;
      this.appendBeatExchange(lines, used, beat, speakers, speakerById, rng, request, target);
      beatIdx++;

      // If a full sweep over the beats stopped adding new (de-duped) lines, fall back
      // to ambient texture so we always make forward progress without repeating.
      stagnation = lines.length > before ? 0 : stagnation + 1;
      if (stagnation > orderedBeats.length) {
        this.appendAmbient(lines, used, speakers, rng, request, target);
        break;
      }
    }

    return lines.slice(0, target);
  }

  private appendBeatExchange(
    out: VoiceLine[],
    used: Set<string>,
    beat: Beat,
    speakers: readonly SpeakerContext[],
    speakerById: Map<string, SpeakerContext>,
    rng: Rng,
    request: VoiceRequest,
    target: number,
  ): void {
    // The player just SAID something (a typed line / quick reply): the town must
    // ANSWER them — greet by name + react to their actual words — not drift into
    // unrelated ambient banter (the "they don't even respond to me" bug, D14). The
    // player is never in the roster, so the generic subject path would mis-handle it.
    if (this.isPlayerMessageBeat(beat)) {
      this.appendPlayerReply(out, used, beat, speakers, rng, request, speakerById, target);
      return;
    }

    // The player just CAST a move (a flourish in the tavern, mirroring show-off): the
    // present residents react with warm, impressed admiration grounded in the SPECIFIC
    // move + its type — never generic banter, never at the player's expense (D14).
    if (this.isPlayerCastBeat(beat)) {
      this.appendCastReply(out, used, beat, speakers, rng, request, speakerById, target);
      return;
    }

    // A quest moment (offer / progress / turn-in): the giver speaks the authored line
    // (grounded, never contradicting canon) and the town cheers warmly — never a nag
    // (D17). Kept focused, like the cast/player-reply paths.
    if (this.isQuestBeat(beat)) {
      this.appendQuestExchange(out, used, beat, speakers, rng, request, speakerById, target);
      return;
    }

    const templates = BEAT_TEMPLATES[beat.kind];

    // The subject speaks first if they're present; otherwise a present speaker
    // narrates the beat warmly.
    const subjectSpeaker = speakerById.get(beat.subjectTrainerId) ?? rng.pick(speakers);

    // For player-facing kinds, the "subject" line is really addressed outward.
    this.pushUnique(out, used, templates.subject, subjectSpeaker, beat, request, rng, speakerById);
    if (out.length >= target) return;

    // Sometimes the subject fondly brags a follow-up about their own mon — builds the
    // multi-turn, peacocking back-and-forth instead of a lone one-liner.
    if (out.length < target && this.beatHasMon(beat) && rng.chance(0.4)) {
      this.pushUnique(out, used, FOND_BRAG_ASIDES, subjectSpeaker, beat, request, rng, speakerById);
      if (out.length >= target) return;
    }

    // One or two warm reactions from *other* present speakers (admiration, two-way
    // curiosity), each guaranteed distinct from everything already said.
    const reactors = rng
      .shuffle(speakers.filter((s) => s.trainerId !== subjectSpeaker.trainerId))
      .slice(0, Math.min(2, Math.max(1, speakers.length - 1)));

    for (const reactor of reactors) {
      if (out.length >= target) break;
      this.pushUnique(
        out,
        used,
        templates.reaction,
        reactor,
        beat,
        request,
        rng,
        speakerById,
        subjectSpeaker,
      );
    }

    if (out.length >= target) return;

    // Weave in a slice of conversational texture so the floor isn't just beat-echoes:
    // peacocking, mutual flattery, a two-way question to the player, or shared
    // know-how — chosen warmly and grounded in beat + player context.
    this.appendTexture(
      out,
      used,
      beat,
      speakers,
      speakerById,
      subjectSpeaker,
      rng,
      request,
      target,
    );
    if (out.length >= target) return;

    // Occasionally fold in a warm memory callback so "they remember you" lands —
    // only when a present speaker actually has a memory and the chance roll passes.
    const rememberers = speakers.filter((s) => s.memorySnippets.length > 0);
    if (out.length < target && rememberers.length > 0 && rng.chance(0.35)) {
      const who = rng.pick(rememberers);
      this.pushUnique(out, used, MEMORY_ASIDES, who, beat, request, rng, speakerById);
    }
  }

  /** True if this beat is the PLAYER's own typed/quick-reply line (player-says, D14). */
  private isPlayerMessageBeat(beat: Beat): boolean {
    return (
      beat.kind === 'chitchat' && beat.data.byPlayer === true && beat.data.topic === 'player-says'
    );
  }

  /** True if this beat is the PLAYER casting a move (a tavern flourish, D14). */
  private isPlayerCastBeat(beat: Beat): boolean {
    return beat.kind === 'chitchat' && beat.data.byPlayer === true && beat.data.topic === 'cast';
  }

  /** True if this beat marks a quest moment (offer / progress / turn-in, D17). */
  private isQuestBeat(beat: Beat): boolean {
    return (
      beat.kind === 'quest-offer' ||
      beat.kind === 'quest-progress' ||
      beat.kind === 'quest-complete'
    );
  }

  /**
   * Render a quest moment to the floor: the GIVER speaks the authored line if they're
   * present (else the warmest present voice relays it — the {giver} token still names
   * the real giver, grounded from beat.data.giverId), then one or two warm reactions
   * cheer the player on. Strictly warm + never a nag: every line lands as "no rush,
   * whenever you like" (D17). The player never speaks an NPC line here.
   */
  private appendQuestExchange(
    out: VoiceLine[],
    used: Set<string>,
    beat: Beat,
    speakers: readonly SpeakerContext[],
    rng: Rng,
    request: VoiceRequest,
    speakerById: Map<string, SpeakerContext>,
    target: number,
  ): void {
    if (speakers.length === 0) return;
    const templates = BEAT_TEMPLATES[beat.kind];

    const giverId = strOf(beat.data.giverId);
    const giver = (giverId && speakerById.get(giverId)) || null;
    // Warmest present voice leads when the giver isn't in the room.
    const order = rng
      .shuffle(speakers.slice())
      .sort((a, b) => b.friendshipWithPlayer - a.friendshipWithPlayer);
    const speaker = giver ?? order[0]!;

    this.pushUnique(out, used, templates.subject, speaker, beat, request, rng, speakerById);
    if (out.length >= target) return;

    const reactors = order.filter((s) => s.trainerId !== speaker.trainerId);
    for (const reactor of reactors.length > 0 ? reactors.slice(0, 2) : [speaker]) {
      if (out.length >= target) break;
      this.pushUnique(
        out,
        used,
        templates.reaction,
        reactor,
        beat,
        request,
        rng,
        speakerById,
        speaker,
      );
    }
  }

  /**
   * Build the town's REACTION to the player casting a move (mirrors
   * {@link appendPlayerReply}): one or two present residents — warmest first — react
   * with impressed admiration grounded in the SPECIFIC move ({move}) + its element
   * ({movetype}) the beat carries, then optionally a two-way invitation so it reads as
   * a real moment. The player never speaks an NPC line (the move rides the beat data,
   * not a speaker slot). Strictly warm — delight only, never a comparison.
   */
  private appendCastReply(
    out: VoiceLine[],
    used: Set<string>,
    beat: Beat,
    speakers: readonly SpeakerContext[],
    rng: Rng,
    request: VoiceRequest,
    speakerById: Map<string, SpeakerContext>,
    target: number,
  ): void {
    if (speakers.length === 0) return;

    // Warmer voices react first (the most delighted lead the cheer), then one more
    // piles on — the little back-and-forth that makes the flourish land.
    const order = rng
      .shuffle(speakers.slice())
      .sort((a, b) => b.friendshipWithPlayer - a.friendshipWithPlayer);
    const reactor = order[0]!;
    this.pushUnique(out, used, CAST_REACTIONS, reactor, beat, request, rng, speakerById);
    if (out.length >= target) return;

    const second = order.find((s) => s.trainerId !== reactor.trainerId) ?? reactor;
    this.pushUnique(out, used, CAST_REACTIONS, second, beat, request, rng, speakerById);
    if (out.length >= target) return;

    // Room to spare ⇒ warmly invite the player to keep going (two-way, never pressure).
    const asker = rng.pick(speakers);
    this.pushUnique(out, used, TWO_WAY_PLAYER_QUESTIONS, asker, beat, request, rng, speakerById);
  }

  /**
   * Build the town's REPLY to the player's own line (D14 two-way). One or two present
   * speakers answer DIRECTLY — addressing the player by name and reacting to what they
   * actually said ({said}) — picking the bucket (greeting / question / general) that
   * matches the message's intent, then a warm follow-up so it reads as a real
   * back-and-forth. The player is never made to speak an NPC line (their `said` rides
   * the beat data, not a speaker slot). Strictly warm, never a comparison.
   */
  private appendPlayerReply(
    out: VoiceLine[],
    used: Set<string>,
    beat: Beat,
    speakers: readonly SpeakerContext[],
    rng: Rng,
    request: VoiceRequest,
    speakerById: Map<string, SpeakerContext>,
    target: number,
  ): void {
    if (speakers.length === 0) return;
    const said = strOf(beat.data.said) ?? '';
    const bank = this.playerReplyBank(said);

    // A friendly speaker answers first (warmer voices lead), then one more piles on.
    const order = rng
      .shuffle(speakers.slice())
      .sort((a, b) => b.friendshipWithPlayer - a.friendshipWithPlayer);
    const responder = order[0]!;
    this.pushUnique(out, used, bank, responder, beat, request, rng, speakerById);
    if (out.length >= target) return;

    // A second voice warmly seconds it — the little back-and-forth that makes the
    // player feel heard. Falls back to the same responder when the room is tiny.
    const second = order.find((s) => s.trainerId !== responder.trainerId) ?? responder;
    this.pushUnique(out, used, PLAYER_REPLY_FOLLOWUPS, second, beat, request, rng, speakerById);
    if (out.length >= target) return;

    // Room to spare ⇒ fold in a two-way question about the player's own team, keeping
    // the conversation flowing and inviting them to say more.
    const asker = rng.pick(speakers);
    this.pushUnique(out, used, TWO_WAY_PLAYER_QUESTIONS, asker, beat, request, rng, speakerById);
  }

  /** Pick the reply bank matching the intent of the player's message text. */
  private playerReplyBank(said: string): readonly TemplateEntry[] {
    const t = said.trim().toLowerCase();
    if (t.length === 0) return PLAYER_GENERAL_REPLIES;
    if (
      /[?]\s*$/.test(t) ||
      /^(who|what|where|when|why|how|is|are|can|could|does|do|any|anyone|has|have)\b/.test(t)
    ) {
      return PLAYER_QUESTION_REPLIES;
    }
    if (
      /\b(hi|hey|hello|yo|howdy|greetings|hiya|sup|heya|good (morning|evening|afternoon))\b/.test(t)
    ) {
      return PLAYER_GREETING_REPLIES;
    }
    return PLAYER_GENERAL_REPLIES;
  }

  /** Weave one piece of conversational texture (D14) into the exchange. */
  private appendTexture(
    out: VoiceLine[],
    used: Set<string>,
    beat: Beat,
    speakers: readonly SpeakerContext[],
    speakerById: Map<string, SpeakerContext>,
    subjectSpeaker: SpeakerContext,
    rng: Rng,
    request: VoiceRequest,
    target: number,
  ): void {
    if (out.length >= target) return;

    // Build the menu of texture kinds available for this moment, then pick one.
    const others = speakers.filter((s) => s.trainerId !== subjectSpeaker.trainerId);
    const choices: Array<'peacock' | 'flattery' | 'twoway' | 'knowhow'> = [
      'peacock',
      'flattery',
      'knowhow',
    ];
    // Two-way questions address the player by name — always welcome.
    choices.push('twoway');

    const kind = rng.pick(choices);
    switch (kind) {
      case 'peacock': {
        // A trainer fondly shows off their own mon (uses beat species when present).
        const who = rng.chance(0.5) && others.length > 0 ? rng.pick(others) : subjectSpeaker;
        this.pushUnique(out, used, PEACOCK_ASIDES, who, beat, request, rng, speakerById);
        break;
      }
      case 'flattery': {
        // Mutual admiration — toward another trainer or the player.
        const who = others.length > 0 ? rng.pick(others) : subjectSpeaker;
        const aboutSubject = others.length > 0 ? rng.pick(speakers) : subjectSpeaker;
        this.pushUnique(
          out,
          used,
          FLATTERY_ASIDES,
          who,
          beat,
          request,
          rng,
          speakerById,
          aboutSubject,
        );
        break;
      }
      case 'twoway': {
        // The town asks the player about *their* Pokémon.
        const who = rng.pick(speakers);
        this.pushUnique(out, used, TWO_WAY_PLAYER_QUESTIONS, who, beat, request, rng, speakerById);
        break;
      }
      case 'knowhow': {
        // Lore / know-how shared as culture.
        const who = rng.pick(speakers);
        this.pushUnique(out, used, KNOWHOW_ASIDES, who, beat, request, rng, speakerById);
        break;
      }
    }
  }

  private appendAmbient(
    out: VoiceLine[],
    used: Set<string>,
    speakers: readonly SpeakerContext[],
    rng: Rng,
    request: VoiceRequest,
    target: number,
  ): void {
    // Cycle the ambient + texture banks so a thin moment still reads as varied,
    // flowing talk — never the same canned line twice.
    const banks: readonly (readonly TemplateEntry[])[] = [
      BEAT_TEMPLATES.chitchat.subject,
      KNOWHOW_ASIDES,
      PEACOCK_ASIDES,
      TWO_WAY_PLAYER_QUESTIONS,
      FLATTERY_ASIDES,
      BEAT_TEMPLATES.chitchat.reaction,
    ];
    let guard = 0;
    let bankIdx = 0;
    while (out.length < target && guard < target * banks.length + banks.length + 8) {
      const speaker = rng.pick(speakers);
      const bank = banks[bankIdx % banks.length]!;
      this.pushUnique(out, used, bank, speaker, this.ambientBeat(), request, rng, new Map());
      bankIdx++;
      guard++;
    }
    // Absolute last resort: if every bank line is exhausted (tiny banks, huge target),
    // emit a uniquified gentle filler so we still satisfy the contract without dupes.
    while (out.length < target) {
      const speaker = rng.pick(speakers);
      const text = this.uniquify(`Lovely to just be here with you all ${out.length + 1}`, used);
      out.push({ trainerId: speaker.trainerId, emotion: 'happy', text });
    }
  }

  /**
   * Pick an entry from `bank` whose rendered text isn't already in `used`, render it,
   * record it, and push it. Tries several distinct entries; if the bank is exhausted
   * for this context, applies a small structural variation so the line still differs.
   * Guarantees no exact-duplicate text within the response.
   */
  private pushUnique(
    out: VoiceLine[],
    used: Set<string>,
    bank: readonly TemplateEntry[],
    speaker: SpeakerContext,
    beat: Beat,
    request: VoiceRequest,
    rng: Rng,
    speakerById: Map<string, SpeakerContext>,
    subjectSpeaker?: SpeakerContext,
  ): void {
    if (bank.length === 0) return;

    // Try each entry (in a seeded shuffle) until one renders to fresh text.
    const order = rng.shuffle(bank);
    for (const entry of order) {
      const line = this.render(entry, speaker, beat, request, rng, speakerById, subjectSpeaker);
      if (!used.has(line.text)) {
        used.add(line.text);
        out.push(line);
        return;
      }
    }

    // Every entry collided (small bank, repeated context). Render one anyway and
    // uniquify it with a gentle variation so the response still carries no dupes.
    const entry = order[0]!;
    const base = this.render(entry, speaker, beat, request, rng, speakerById, subjectSpeaker);
    const text = this.uniquify(base.text, used);
    used.add(text);
    out.push({ ...base, text });
  }

  /** Append a soft, distinct flourish until the text is unused. Deterministic. */
  private uniquify(text: string, used: Set<string>): string {
    if (!used.has(text)) return text;
    for (const f of FLOURISHES) {
      const candidate = `${text} ${f}`.replace(/[ \t]{2,}/g, ' ').trim();
      if (!used.has(candidate)) return candidate;
    }
    // Pad with non-printable-free spaces of varying count as a final tie-breaker.
    let n = 1;
    let candidate = text;
    while (used.has(candidate)) {
      candidate = `${text} (${++n})`;
    }
    return candidate;
  }

  private render(
    entry: TemplateEntry,
    speaker: SpeakerContext,
    beat: Beat,
    request: VoiceRequest,
    rng: Rng,
    speakerById: Map<string, SpeakerContext>,
    subjectSpeaker?: SpeakerContext,
  ): VoiceLine {
    const subject = subjectSpeaker ?? speakerById.get(beat.subjectTrainerId) ?? speaker;

    const species = titleCase(strOf(beat.data.speciesId) ?? rng.pick(NICK_FLAVOR_SPECIES));
    const from = titleCase(strOf(beat.data.fromSpeciesId) ?? 'tiny little self');
    const item = titleCase(strOf(beat.data.itemId) ?? 'something nice');
    const level = numOf(beat.data.level) ?? 5 + rng.int(20);
    const nick = strOf(beat.data.nickname) ?? rng.pick(NICK_FLAVOR);
    const rare = titleCase(strOf(beat.data.rareSpeciesId) ?? rng.pick(RARE_FLAVOR));
    const biome = strOf(beat.data.biome) ?? rng.pick(BIOME_FLAVOR);
    // The move the player just cast (cast beats); fall back warmly so a stray
    // {move}/{movetype} token in any bank still renders cleanly.
    const move = titleCase(
      strOf(beat.data.moveName) ?? strOf(beat.data.moveId) ?? rng.pick(MOVE_FLAVOR),
    );
    const moveType = titleCase(strOf(beat.data.moveType) ?? rng.pick(MOVE_TYPE_FLAVOR));
    const deed =
      request.player.recentDeeds.length > 0
        ? rng.pick(request.player.recentDeeds)
        : 'showed up and made the day brighter';
    // A Pokémon the player is "known for" — derive from a deed mention or fall back warmly.
    const pmon = this.playerMon(request, rng);
    const memory = speaker.memorySnippets.length > 0 ? rng.pick(speaker.memorySnippets) : 'said hi';
    // The player's own words (for player-reply templates), trimmed to a chat-bubble
    // length and scrubbed so even an unkind message echoes back warm. Empty when the
    // beat carries none — the {said} token then collapses cleanly.
    const said = this.echoSaid(strOf(beat.data.said));
    // Quest-beat tokens (D17). {giver} resolves from beat.data.giverId to a present
    // speaker's name, else the id title-cased, else the subject — so it always names
    // the real giver even when they aren't in the room. The authored {line} is kept
    // verbatim (only scrubbed for warmth). Objective/reward/title are warm summaries
    // the questBeat factory computed, so the voice can never contradict quest canon.
    const giverId = strOf(beat.data.giverId);
    // A raw ref/id ('t106', 'role:rival', 'archetype:…') must never surface in chat —
    // if the giver isn't a present speaker, fall back to the subject's real name.
    const looksLikeRawId = (id: string): boolean =>
      /^t\d+$/i.test(id) || id.includes(':') || /^npc-/.test(id);
    const giver = giverId
      ? (speakerById.get(giverId)?.username ??
        (looksLikeRawId(giverId) ? subject.username : titleCase(giverId)))
      : subject.username;
    const line = scrubLine(strOf(beat.data.line) ?? '')
      .replace(/\s+/g, ' ')
      .trim();
    const questTitle = strOf(beat.data.questTitle) ?? 'a little adventure';
    const objective = strOf(beat.data.objective) ?? 'this next little step';
    const reward = strOf(beat.data.reward) ?? 'a warm thank-you';
    // The follower-care reaction verb ("munches happily", "does a happy skip") a
    // follower-mood beat carries, so town chat + welcome-back letters read warmly.
    const verb = strOf(beat.data.verb) ?? 'looks so happy';
    // Flourishes appear only ~half the time so the chat doesn't feel decorated.
    const emoji = rng.chance(0.5) ? rng.pick(FLOURISHES) : '';

    let text = entry.text
      .replace(/\{self\}/g, speaker.username)
      .replace(/\{subject\}/g, subject.username)
      .replace(/\{player\}/g, request.player.playerName)
      .replace(/\{giver\}/g, giver)
      .replace(/\{line\}/g, line)
      .replace(/\{questtitle\}/g, questTitle)
      .replace(/\{objective\}/g, objective)
      .replace(/\{reward\}/g, reward)
      .replace(/\{said\}/g, said)
      .replace(/\{species\}/g, species)
      .replace(/\{from\}/g, from)
      .replace(/\{nick\}/g, nick)
      .replace(/\{level\}/g, String(level))
      .replace(/\{item\}/g, item)
      .replace(/\{rare\}/g, rare)
      .replace(/\{biome\}/g, biome)
      .replace(/\{verb\}/g, verb)
      .replace(/\{move\}/g, move)
      .replace(/\{movetype\}/g, moveType)
      .replace(/\{deed\}/g, deed)
      .replace(/\{pmon\}/g, pmon)
      .replace(/\{memory\}/g, memory)
      .replace(/\{emoji\}/g, emoji);

    // Tidy spacing left by an empty {emoji} and scrub for warmth (the floor must
    // never emit unkindness even by authoring mistake).
    text = scrubLine(text)
      .replace(/\s+([.,!?])/g, '$1')
      .replace(/[ \t]{2,}/g, ' ')
      .trim();

    return {
      trainerId: speaker.trainerId,
      emotion: this.tintEmotion(entry.emotion, speaker),
      text,
    };
  }

  /**
   * Render the player's own words for echoing back in a reply: scrub for warmth (so an
   * unkind shout still echoes kind), collapse whitespace, strip a trailing '?' (the
   * template supplies its own punctuation/quotes), and clamp to a chat-bubble length so
   * a long paste never bloats a line. Empty in ⇒ empty out (the {said} token collapses).
   */
  private echoSaid(raw: string | undefined): string {
    if (!raw) return '';
    let s = scrubLine(raw).replace(/\s+/g, ' ').trim();
    s = s.replace(/[?.!\s]+$/g, '');
    const MAX = 60;
    if (s.length > MAX) s = s.slice(0, MAX).trimEnd() + '…';
    return s;
  }

  /** A Pokémon the player is associated with, for two-way questions/flattery. Pulls a
   * species mentioned in a recent deed when possible; otherwise a warm stand-in. */
  private playerMon(request: VoiceRequest, rng: Rng): string {
    const deeds = request.player.recentDeeds;
    for (const d of deeds) {
      const m = /\b([A-Z][a-z]+)\b/.exec(d);
      if (m && m[1] && m[1] !== request.player.playerName) return m[1];
    }
    return rng.pick(['your team', 'your starter', 'your newest catch']);
  }

  private beatHasMon(beat: Beat): boolean {
    return typeof beat.data.speciesId === 'string' && beat.data.speciesId.length > 0;
  }

  /** Nudge emotion warmer for very-friendly speakers; always pass through the
   * safety softener so nothing reads as hostility at the player. */
  private tintEmotion(base: Emotion, speaker: SpeakerContext): Emotion {
    let e = base;
    if (speaker.friendshipWithPlayer >= 80 && (base === 'normal' || base === 'happy')) {
      e = 'joyous';
    }
    return softenEmotion(e);
  }

  private ambientBeat(): Beat {
    return {
      id: 'ambient',
      kind: 'chitchat',
      priority: 'ambient',
      subjectTrainerId: '',
      channel: 'global',
      data: {},
      createdAt: 0,
    };
  }

  private requestSeed(request: VoiceRequest): number {
    const beatKey = request.beats.map((b) => b.id).join(',');
    const speakerKey = request.speakers.map((s) => s.trainerId).join(',');
    const playerKey = request.player.playerName + '|' + request.player.recentDeeds.join(',');
    return (
      (this.seed ^
        hashString(`${beatKey}::${speakerKey}::${playerKey}::${request.desiredLineCount}`)) >>>
      0
    );
  }
}

/** Soft species flavor when a texture line wants a {species} but the beat has none. */
const NICK_FLAVOR_SPECIES: readonly string[] = [
  'Eevee',
  'Pikachu',
  'Bulbasaur',
  'Vulpix',
  'Growlithe',
  'Oddish',
];

function strOf(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function numOf(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

function titleCase(s: string): string {
  return s.replace(
    /(^|[-_\s])([a-z])/g,
    (_m, sep: string, ch: string) => (sep === '-' || sep === '_' ? ' ' : sep) + ch.toUpperCase(),
  );
}
